import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
/*
 * https://popper.js.org/docs/v2/
 * https://popper.js.org/react-popper/
 */
import { usePopper } from 'react-popper';
import { generateUUID } from '../../../helpers/generateUUID';
import useEventHandler from '../../customHooks/useEventHandler';
import modalKeyboardHandler from '../../../helpers/modalKeyboardHandler';
import Inset from '../../spacing/Inset/Inset';
import { TooltipArrow, TooltipContent, TooltipTrigger } from './subcomponents';
import { ESCAPE_KEY } from '../../../const/keyNames';
const POPPER_ARROW_OFFSET = 30;
const POPPER_PLACEMENT_DISTANCE_OFFSET = 20;
const NO_OFFSET = 0;
const DELAY_TOOLTIP_CLOSE = 100;
/*
 * https://popper.js.org/docs/v2/modifiers/offset/
 * We have to offset the arrow to account for the tooltip border-radius
 * so we offset the skidding to position the arrow directly over the trigger
 */
const getPlacementSkiddingOffset = (placement) => {
    if (placement.includes('-end')) {
        return POPPER_ARROW_OFFSET;
    }
    if (placement.includes('-start')) {
        return -POPPER_ARROW_OFFSET;
    }
    return NO_OFFSET;
};
const Tooltip = ({ isInteractive, placement = 'top-start', hasBorder = true, content, className, children, id = generateUUID(), ariaLabel }) => {
    const [popperReference, setPopperReference] = useState(null);
    const [popperElement, setPopperElement] = useState(null);
    const [arrowElement, setArrowElement] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    /*
     * delaying the closing of the tooltip allows users to transition their cursor from hovering over
     * the trigger to hovering over the tooltip itself and allowing it to stay open
     */
    const closeTooltipTimeout = () => setTimeout(() => setIsOpen(false), DELAY_TOOLTIP_CLOSE);
    let closeTooltipTimeoutRef;
    useEventHandler('keydown', (event) => modalKeyboardHandler(event, { escapeHandler: closeTooltipTimeout }));
    /*
     * https://popper.js.org/react-popper/v2/faq/
     * We use memo here to avoid infnite render loops
     * We use the currentPlacement instead of the placement prop
     * to determine the offset so to allow the offsets to be calculated correctly
     * if the tooltip needs to be auto positioned
     */
    const offsetModifier = useMemo(() => ({
        name: 'offset',
        options: {
            offset: ({ placement: currentPlacement }) => {
                return [
                    getPlacementSkiddingOffset(currentPlacement),
                    POPPER_PLACEMENT_DISTANCE_OFFSET
                ];
            }
        }
    }), []);
    const { styles: popperStyles, attributes: popperAttributes } = usePopper(popperReference, popperElement, {
        placement,
        modifiers: [
            {
                name: 'arrow',
                options: { element: arrowElement, padding: POPPER_ARROW_OFFSET }
            },
            offsetModifier
        ]
    });
    const triggerProps = {
        tabIndex: 0,
        onMouseEnter: () => {
            setIsOpen(true);
            clearTimeout(closeTooltipTimeoutRef);
        },
        onMouseLeave: () => {
            closeTooltipTimeoutRef = closeTooltipTimeout();
        },
        onFocus: () => setIsOpen(true),
        onBlur: () => {
            closeTooltipTimeoutRef = closeTooltipTimeout();
        },
        'aria-describedby': `tooltip-${id}`,
        onKeyDown: (event) => event.key === ESCAPE_KEY && closeTooltipTimeout()
    };
    let interactiveTriggerWithProps;
    /*
     * If it's an interactive trigger then we just apply the triggerProps directly,
     * otherwise we force the trigger to be interactive so that it stays accessible
     */
    if (isInteractive) {
        const tooltipTrigger = React.Children.only(children);
        interactiveTriggerWithProps = React.cloneElement(tooltipTrigger, triggerProps);
    }
    else {
        triggerProps.role = 'button';
        triggerProps['aria-label'] = ariaLabel || 'tooltip';
    }
    return (_jsxs(_Fragment, { children: [_jsx(TooltipTrigger, Object.assign({ ref: setPopperReference }, (isInteractive ? {} : triggerProps), { children: isInteractive ? interactiveTriggerWithProps : children })), isOpen && content && (_jsxs(TooltipContent, Object.assign({ className: className, id: `tooltip-${id}`, role: "tooltip", ref: setPopperElement, style: popperStyles.popper }, popperAttributes.popper, { hasBorder: hasBorder, onMouseEnter: () => {
                    setIsOpen(true);
                    clearTimeout(closeTooltipTimeoutRef);
                }, onMouseLeave: () => {
                    closeTooltipTimeoutRef = closeTooltipTimeout();
                } }, { children: [_jsx(Inset, Object.assign({ top: "medium", right: "medium", bottom: "small", left: "base" }, { children: _jsx("span", { children: content }) })), _jsx(TooltipArrow, { ref: setArrowElement, style: popperStyles.arrow, hasBorder: hasBorder })] })))] }));
};
export default Tooltip;
//# sourceMappingURL=Tooltip.js.map